import {StackProps} from '@mui/material/Stack';
import {SvgIconProps} from '@mui/material/SvgIcon/SvgIcon';
import Typography, {TypographyProps} from '@mui/material/Typography';
import {Theme} from '@mui/system/createTheme';
import {SxProps} from '@mui/system/styleFunctionSx';
import {ConnectionStatus, IMachineStateDTO, MachineStatus} from 'api/api';
import dayjs from 'dayjs';
import {localized} from 'i18n/i18n';
import React, {useMemo} from 'react';
import {MachineStateMapper} from 'utilities/mappers/machine-state-mapper';
import IconLabelPlaceholder from 'view/components/specific-components/icon-label-placeholder';

// keyof Translation to ensure rerendering when chaning language

type Variant = 'machine' | 'machine-details';

const iconStyles: SxProps<Theme> = {color: 'common.flexoWashBlueLight', fontSize: '2.5rem'};

const variantMapper: Record<Variant, TypographyProps> = {
  machine: {
    fontSize: '1rem',
  },
  'machine-details': {
    fontSize: '1.25rem',
  },
};

interface Props extends StackProps {
  machineCurrentState?: IMachineStateDTO;
  variant?: Variant;
  iconProps?: SvgIconProps;
}

export const MachineState: React.FC<Props> = (props) => {
  const {children, variant = 'machine', iconProps, machineCurrentState, ...rest} = props;

  const machineState = useMemo(
    () =>
      machineCurrentState?.machineStatus !== undefined &&
      machineCurrentState?.connectionStatus !== ConnectionStatus.Offline
        ? machineCurrentState?.machineStatus
        : 'Unknown',
    [machineCurrentState?.connectionStatus, machineCurrentState?.machineStatus],
  );

  const {stateTranslationKey: stateStatus, icon: StateIcon} = useMemo(
    () => MachineStateMapper[machineState],
    [machineState],
  );

  const showTimeRemaining = useMemo(
    () =>
      machineCurrentState?.machineStatus === MachineStatus.Running &&
      !!machineCurrentState.washCompleteAt &&
      dayjs().isBefore(machineCurrentState?.washCompleteAt),
    [machineCurrentState?.machineStatus, machineCurrentState?.washCompleteAt],
  );

  return (
    <IconLabelPlaceholder icon={<StateIcon sx={iconStyles} {...iconProps} />} {...rest}>
      <Typography
        variant="body3"
        color="common.flexoWashBlue"
        textTransform="uppercase"
        fontWeight={500}
        {...variantMapper[variant]}>
        {localized(stateStatus)}
      </Typography>
      {showTimeRemaining ? (
        <Typography variant="body3" color="common.flexoWashBlueLight">
          {Math.round(dayjs(machineCurrentState?.washCompleteAt).diff(dayjs(), 'seconds') / 60)}
          {' ' + localized('MinutesRemaining').toLowerCase()}
        </Typography>
      ) : (
        <Typography variant="body3" color="common.flexoWashBlueLight">
          {localized('MachineStatus')}
        </Typography>
      )}
    </IconLabelPlaceholder>
  );
};
